import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMemo, useState } from 'react';

import { inputStyles } from '@/styles/input';
import { linkStyles } from '@/styles/links';
import { fontStyles } from '@/styles/typography';

import { SearchIcon } from '@/ui/Icons/SearchIcon';

import { classNames } from '@/utils/classnames';

import { DismissalMode, MultiselectItem } from '..';
import { MultiselectOption } from '../MultiselectOption';

export type MultiselectContentProps = {
  title?: string;
  showSelectAllButton?: boolean;
  value: string[];
  items: MultiselectItem[];
  onChange: (value: string[]) => void;
  modalVariant?: boolean;
  itemsLayout?: 'default' | 'two-columns';
  search?: { emptyLabel: string };
  isLoading?: boolean;
  multiselect?: boolean;
  onDismissalModeChange: (mode: DismissalMode) => void;
};

const loadingItems: MultiselectItem[] = Array.from({ length: 6 }, (_, index) => ({
  label: index.toString(),
  value: index.toString(),
  icon: '',
  iconAlt: '',
}));

export const MultiselectContent = ({
  title,
  showSelectAllButton,
  value,
  items,
  onChange,
  modalVariant,
  itemsLayout = 'default',
  search,
  isLoading,
  multiselect,
  onDismissalModeChange,
}: MultiselectContentProps) => {
  const [searchValue, setSearchValue] = useState('');

  const toggleSelection = (selection: string) => {
    const newValue = value.includes(selection)
      ? value.filter((item) => item !== selection)
      : value.concat(selection);
    onChange(newValue);
  };

  const handleSelectAllClick = () => {
    const newValue = showDeselectButton ? [] : items.map((item) => item.value);
    onChange(newValue);
  };

  const handleItemSelect = (item: MultiselectItem) => (event: Event) => {
    if (multiselect) {
      // Prevent the dropdown from closing
      event.preventDefault();
      toggleSelection(item.value);
    } else {
      onChange([item.value]);
    }
  };

  // Update dismissal mode to correctly handle auto-focus on dropdown close
  const handleItemClick = () => {
    if (multiselect) {
      onDismissalModeChange('pointer');
    }
  };

  const filteredItems = useMemo(() => {
    if (isLoading) {
      return loadingItems;
    }

    return items.filter(
      (item) => !searchValue || item.label.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [items, searchValue, isLoading]);

  const inputClasses = inputStyles();
  const showDeselectButton = value.length > 0;

  return (
    <div
      className={classNames('flex flex-col tablet:h-auto', {
        'mt-1 px-4 pt-3 pb-6': modalVariant,
        'h-[90dvh] tablet:h-auto': search != null,
      })}
    >
      {(title || showSelectAllButton) && (
        <div
          className={classNames(
            'flex flex-row justify-between items-center',
            modalVariant ? 'py-4' : 'py-2 px-3',
          )}
        >
          {title != null && (
            <p className={fontStyles({ variant: modalVariant ? 'H5' : 'LABEL_SM' })}>{title}</p>
          )}
          {showSelectAllButton && (
            <button
              className={linkStyles({
                color: 'purple',
                underline: true,
                font: modalVariant ? 'LABEL_MD' : 'LABEL_SM',
              })}
              onClick={handleSelectAllClick}
            >
              {showDeselectButton ? 'Deselect all' : 'Select all'}
            </button>
          )}
        </div>
      )}
      {search && (
        <div
          className={inputClasses.root({
            class: [
              'mb-1 mt-2 tablet:mt-0 shrink-0 focus-within:outline-none',
              isLoading ? 'focus-within:border-token-alphalight-500' : 'hover:shadow-none',
            ],
          })}
        >
          <span className={inputClasses.accessoryLeft()}>
            <SearchIcon className="size-4" />
          </span>
          <input
            type="string"
            className={inputClasses.input()}
            placeholder="Search"
            value={searchValue}
            disabled={isLoading}
            onKeyDown={(event) => event.stopPropagation()}
            onChange={(event) => setSearchValue(event.target.value)}
          />
        </div>
      )}
      <div
        className={classNames(
          itemsLayout === 'two-columns' ? 'grid grid-cols-[1fr_1fr]' : 'flex flex-col',
          { 'h-full tablet:h-[250px]': search != null },
        )}
      >
        {filteredItems.map((item) =>
          modalVariant ? (
            <MultiselectOption
              key={item.value}
              item={item}
              selected={value.includes(item.value)}
              onClick={() => (multiselect ? toggleSelection(item.value) : onChange([item.value]))}
              isLoading={isLoading}
              multiselect={multiselect}
            />
          ) : (
            <DropdownMenu.Item
              key={item.value}
              asChild={true}
              onSelect={handleItemSelect(item)}
              onClick={handleItemClick}
            >
              <MultiselectOption
                item={item}
                selected={value.includes(item.value)}
                isLoading={isLoading}
                multiselect={multiselect}
              />
            </DropdownMenu.Item>
          ),
        )}
        {search != null && filteredItems.length === 0 && isLoading && (
          <div className="flex items-center justify-center h-full">
            <p className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-800' })}>
              {search.emptyLabel}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
